import {Injectable, signal} from '@angular/core';
import {GrafikUmfang} from '../model/event-bus.model';

/** Ein erzeugter Text: einer je Ziel-Textbox. */
export interface KiTextEintrag {
  /** Beschriftung der Ziel-Textbox ("Heute", "Schlagzeile"); leer, wenn keine gefunden wurde. */
  ziel: string;
  /** Der erzeugte Text selbst. */
  text: string;
  /** Aus wie vielen Boxen die Anfrage gebaut wurde, die ihn erzeugt hat. */
  boxen: number;
  /** Ist er in seine Textbox übernommen worden? */
  uebernommen: boolean;
  /** Wann er eintraf – jeder Text hat seinen eigenen Zeitpunkt. */
  zeitpunkt: Date;
}

/**
 * Was ein Knopfdruck erzeugt hat, wie es die grüne Box anzeigt.
 *
 * <b>Mehrere Texte je Antwort sind der Normalfall, nicht die Ausnahme:</b> im Reiter
 * "Heute" erzeugt ein Druck zwei Texte – den Heute-Text und die Schlagzeile –, und beide
 * gehören in denselben grünen Preview-Kasten. Deshalb ist `texte` eine Liste und kein einzelnes Feld.
 * Die zwei Anfragen laufen **parallel**, treffen also in beliebiger Reihenfolge ein; sie
 * tragen sich über {@link KiErgebnisService#ergaenze} nach und nach ein.
 */
export interface KiErgebnis {
  /** Beschriftung des Umfangs, aus dem die Anfragen kamen (Reiter- bzw. Zeilenname). */
  name: string;
  /** Die erzeugten Texte in der Reihenfolge ihres Eintreffens. */
  texte: KiTextEintrag[];
}

/**
 * Die KI-Antworten, damit die grünen Boxen sie anzeigen können – **eine je Umfang**.
 *
 * Es gibt den Knopf "KI-Texte erzeugen" zweimal: in der Leiste oben, wo er die
 * angehakten Boxen **seines Reiters** einsammelt, und an jeder Zeilenüberschrift, wo er
 * nur die seiner eigenen Zeile nimmt. Beide Antworten dürfen gleichzeitig sichtbar sein,
 * jede in ihrer eigenen Box – deshalb liegen sie in einer Map und nicht in einem Feld.
 *
 * Geschlüsselt wird über {@link GrafikUmfang#schluessel}, also Art plus erste
 * `containerId` – nicht über die Beschriftung: die Namen wiederholen sich zwischen den
 * Bereichen ("HEUTE: ganz" gibt es in NRW, in COSMO und in jeder Lokalzeit-Stadt), und
 * einmal geöffnete Reiter bleiben gerendert. Die Art gehört dazu, weil der Reiter-Umfang
 * und der seiner ersten Zeile mit derselben Karte beginnen.
 *
 * Die Map liegt in einem **Signal** und nicht in einem Feld: Auslöser
 * ({@link DocumentDetailComponent}) und Anzeige ({@link KiErgebnisBoxComponent}) sind
 * Geschwister-Ansichten, und die Antwort trifft aus einem HTTP-Callback ein – also
 * außerhalb jeder Template-Bindung und jedes DOM-Ereignisses. Ein Signal markiert die
 * lesende Ansicht selbst als geändert; mit einem gewöhnlichen Feld hinge es daran, ob
 * die Prüf-Traversierung die Zeile erreicht (siehe `KartenZoomService`, dasselbe Muster
 * aus demselben Grund).
 */
@Injectable({providedIn: 'root'})
export class KiErgebnisService {

  private readonly ergebnisse = signal<ReadonlyMap<string, KiErgebnis>>(new Map());

  /** Die Antwort für diesen Umfang, oder null solange keine vorliegt. */
  ergebnisFuer(umfang: GrafikUmfang | null | undefined): KiErgebnis | null {
    return umfang ? this.ergebnisse().get(umfang.schluessel) ?? null : null;
  }

  /**
   * Einen eingetroffenen Text anzeigen – **zusätzlich** zu denen, die für diesen Umfang
   * schon dastehen.
   *
   * Anfügen statt ersetzen, weil im Reiter "Heute" zwei Anfragen parallel laufen und in
   * beliebiger Reihenfolge antworten. Was vom vorigen Knopfdruck stehen blieb, verwirft
   * die Aufrufstelle vorher mit {@link leeren}.
   */
  ergaenze(umfang: GrafikUmfang, text: string, boxen: number,
           ziel: string | null): KiErgebnis {
    const eintrag: KiTextEintrag = {ziel: ziel ?? '', text, boxen, uebernommen: !!ziel,
      zeitpunkt: new Date()};
    const vorhanden = this.ergebnisFuer(umfang);
    const ergebnis: KiErgebnis = {
      name: umfang.name,
      texte: [...(vorhanden?.texte ?? []), eintrag]
    };
    this.setze(umfang, ergebnis);
    return ergebnis;
  }

  /**
   * Eine vollständige Antwort setzen – der Weg für die aus dem Datensatz
   * wiederhergestellten Boxen, die ihren **ursprünglichen** Zeitpunkt behalten sollen.
   */
  setze(umfang: GrafikUmfang, ergebnis: KiErgebnis): void {
    const naechste = new Map(this.ergebnisse());
    naechste.set(umfang.schluessel, ergebnis);
    this.ergebnisse.set(naechste);
  }

  /**
   * Eine Box wieder ausblenden – vom Schließen-Knopf und vom Start einer neuen Anfrage
   * desselben Umfangs.
   *
   * Letzteres ist wichtig: bliebe die alte Antwort stehen, während die neue läuft, wäre
   * nicht zu erkennen, welche der beiden man gerade liest.
   */
  leeren(umfang: GrafikUmfang | null | undefined): void {
    if (!umfang) {
      return;
    }
    const naechste = new Map(this.ergebnisse());
    if (naechste.delete(umfang.schluessel)) {
      this.ergebnisse.set(naechste);
    }
  }

  /**
   * Alle Antworten verwerfen.
   *
   * Läuft bei **jedem** Laden eines Dokuments: der Service lebt in der Wurzel und
   * überdauert den Wechsel der Seite, sonst stünden über den Karten noch die Texte des
   * vorher bearbeiteten Dokuments. Was zum geladenen Datensatz gehört, trägt
   * `DocumentDetailComponent` danach aus dem Dokumentbaum wieder ein
   * ({@link KiGrafikService#ladeErgebnisse}) – bei einem **neuen** Datensatz ist da
   * nichts, die Boxen bleiben also weg.
   */
  alleLeeren(): void {
    if (this.ergebnisse().size > 0) {
      this.ergebnisse.set(new Map());
    }
  }
}
